import { fetchContact } from "@/services/supabaseService";
import { useQuery } from "@tanstack/react-query";
import { Mail, MessageSquare, Github, Linkedin, Youtube } from "lucide-react";
import { Key } from "react";

const ContactInformation = () => {
  const { data: contact } = useQuery({
    queryKey: ["contact"],
    queryFn: fetchContact,
  });

  return (
    <>
      <div
        className="glass-morphism rounded-xl p-6 animate-fade-in"
        style={{ animationDelay: "0.4s" }}
      >
        <h2 className="text-xl font-semibold mb-6 text-white">
          Contact Information
        </h2>

        <div className="space-y-5">
          <div className="flex items-start">
            <div className="p-2.5 rounded-md bg-white/[0.05] mr-4">
              <Mail className="h-5 w-5 text-teal" />
            </div>
            <div>
              <h3 className="text-gray-300 font-medium">Email</h3>
              <a
                href={`mailto:${contact?.email}`}
                className="text-gray-400 hover:text-teal transition-colors"
              >
                {contact?.email}
              </a>
            </div>
          </div>

          <div className="flex items-start">
            <div className="p-2.5 rounded-md bg-white/[0.05] mr-4">
              <MessageSquare className="h-5 w-5 text-teal" />
            </div>
            <div>
              <h3 className="text-gray-300 font-medium">Social Profiles</h3>
              <div className="flex space-x-3 mt-2">
                <a
                  href={`https://github.com/${contact?.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-md bg-white/[0.03] hover:bg-white/[0.08] transition-colors"
                  aria-label="Visit GitHub profile"
                  title={`GitHub: ${contact?.github ?? ''}`}
                >
                  <Github className="h-5 w-5 text-gray-400 hover:text-white" />
                  <span className="sr-only">GitHub</span>
                </a>
                <a
                  href={`https://linkedin.com/in/${contact?.linked_in}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-md bg-white/[0.03] hover:bg-white/[0.08] transition-colors"
                  aria-label="Visit LinkedIn profile"
                  title={`LinkedIn: ${contact?.linked_in ?? ''}`}
                >
                  <Linkedin className="h-5 w-5 text-gray-400 hover:text-white" />
                  <span className="sr-only">LinkedIn</span>
                </a>
                <a
                  href={`https://youtube.com/@${contact?.youtube ?? contact?.linked_in ?? ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-md bg-white/[0.03] hover:bg-white/[0.08] transition-colors"
                  aria-label="Visit YouTube channel"
                  title={`YouTube: ${contact?.youtube ?? ''}`}
                >
                  <Youtube className="h-5 w-5 text-gray-400 hover:text-white" />
                  <span className="sr-only">YouTube</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="glass-morphism rounded-xl p-6 animate-fade-in"
        style={{ animationDelay: "0.5s" }}
      >
        <h2 className="text-xl font-semibold mb-4 text-white">Available For</h2>

        <div className="space-y-3">
          {contact?.available_for?.map((item: string, index: Key) => (
            <div className="flex items-center" key={index}>
              <div className="h-2 w-2 rounded-full bg-teal mr-3"></div>
              <span className="text-gray-300">{item}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 text-gray-400 text-sm">
          <p>Currently based in {contact?.based_in}</p>
          {contact?.open_to_work && (
            <p className="mt-1">Open to opportunities</p>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactInformation;
